import styles from '../index.module.scss'
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    Skeleton,
    Stack,
    styled,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
    selectIncompleteAccreditationDocIds,
    selectTeacherAccreditationDocumentByTypes,
    selectTeacherDob,
    selectTeacherEmploymentSettings,
    selectTeacherNames,
    selectTeacherSetLocation,
} from '../../../../redux/teacherSlice'
import LoadingSpinner from '../../../../components/LoadingSpinner/LoadingSpinner'
import {
    Control,
    Controller,
    FieldValues,
    Path,
    useForm,
} from 'react-hook-form'
import z from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import pxToRem from '../../../../helpers/pxToRem'
import FormInputText from '../../../../components/FormComponents/FormInputText'
import {
    PrimaryOutlinedButton,
    PrimaryButton,
} from '../../../../components/Button'
import { SchoolRegSx, UserRegSx } from '../../../../components/TextField'
import { selectUserUid } from '../../../../redux/userSlice'
import { ref, update } from 'firebase/database'
import { db } from '../../../../firebase/configs'
import { setErrorMsg } from '../../../../redux/uiSlice'
import { getError } from '../../../../helpers/utils'
import dayjs from '../../../../helpers/dayjs'
import { useEffect, useRef, useState } from 'react'
import { EmploymentSettings } from '../../../../firebase/types-teacher'
import { DayjsSchema } from '../../../../schemas/helper'
import { useNavigate } from 'react-router-dom'
import FormDatePicker_v2 from '../../../../components/FormComponents/FormDatePicker_v2'
import { logger } from '../../../../helpers/logger'
import { TEACHER_API } from '../../../../firebase/teacherApi'
import { captureException } from '@sentry/react'
import {
    CheckComponent,
    GreenCheckedIcon,
    YellowCheckedIcon,
} from '../../../../components/Checkbox'

export const DeclarationFormWrapper = ({ onBack }: { onBack: () => void }) => {
    const setLocation = useSelector(selectTeacherSetLocation)
    const setting = useSelector(selectTeacherEmploymentSettings)

    const [status, setStatus] = useState<
        | { loading: true; variant?: never }
        | { loading: false; variant: 'simple' | 'vic' }
    >({ loading: true })

    useEffect(() => {
        if (setLocation.status !== 'success' || setting.status !== 'success') {
            setStatus({
                loading: true,
            })
            return
        }

        if (
            setLocation.data.homeState === 'VIC' &&
            (Object.values(setting.data).some((el) => el.type === 'school') ||
                Object.values(setting.data).some(
                    (el) =>
                        el.type === 'earlyChildcare' &&
                        el.level === 'earlyChildhoodTrained'
                ))
        ) {
            // Calculate state for vic dec form
            setStatus({
                loading: false,
                variant: 'vic',
            })
        } else {
            setStatus({
                loading: false,
                variant: 'simple',
            })
        }
    }, [setLocation, setting])

    if (status.loading)
        return <LoadingSpinner fillViewPortHeight fillViewPortWidth />

    if (status.variant === 'vic') return <VICDeclarationForm onBack={onBack} />
    return <SimpleDeclarationForm onBack={onBack} />
}

interface IDeclartionForm {
    onBack: () => void
}

const simpleFormSchema = z
    .object({
        hasLimitations: z.boolean(),
        moreContext: z.string(),
        consent: z.boolean().refine((v) => Boolean(v)),
    })
    .check((ctx) => {
        const moreContextLength = ctx.value.moreContext.trim().length

        if (
            !(
                moreContextLength === 0 ||
                (moreContextLength >= 50 && moreContextLength <= 400)
            )
        ) {
            ctx.issues.push({
                code: 'custom',
                message: 'Please meet the character requirement below',
                input: ctx.value.moreContext,
                path: ['moreContext'],
            })
        }

        if (ctx.value.hasLimitations && moreContextLength === 0) {
            ctx.issues.push({
                code: 'custom',
                message: 'Context required',
                input: ctx.value.moreContext,
                path: ['moreContext'],
            })
        }
    })

export const SimpleDeclarationForm = ({ onBack }: IDeclartionForm) => {
    const {
        watch,
        handleSubmit,
        control,
        formState: { isSubmitting, isValid },
    } = useForm({
        resolver: zodResolver(simpleFormSchema),
        mode: 'onChange',
        defaultValues: {
            moreContext: '',
        },
    })

    const isMobile = useMediaQuery('(max-width:600px')
    const hasLimitation = watch('hasLimitations')
    const moreContext = watch('moreContext')

    const userId = useSelector(selectUserUid)
    const employmentSetting = useSelector(selectTeacherEmploymentSettings)
    const setLocation = useSelector(selectTeacherSetLocation)
    const incompleteAccreditationDocs = useSelector(
        selectIncompleteAccreditationDocIds
    )
    const dispatch = useDispatch()

    const onSubmit = async (data: z.infer<typeof simpleFormSchema>) => {
        try {
            // Add delay
            await new Promise((res) => setTimeout(res, 3000))
            // Submit declaration
            if (!userId) {
                throw new Error('User not authenticated')
            }
            if (setLocation.status !== 'success') {
                throw new Error('User not setlocation')
            }
            const homeState = setLocation.data.homeState

            const empSettingUpdates = Object.keys(
                employmentSetting.data || {}
            ).reduce(
                (a, c) => ({
                    ...a,
                    [`employmentSetting/${c}/state`]: 'submitted',
                }),
                {}
            )

            const current = dayjs().tz()

            const dbUpdates: Record<string, any> = {
                //#region Employment Declarations
                [`/employmentDeclarations/${homeState}/hasLimitations`]:
                    data.hasLimitations,
                [`/employmentDeclarations/${homeState}/state`]: 'submitted',
                [`/employmentDeclarations/${homeState}/timeDeclared`]:
                    current.unix(),
                [`/employmentDeclarations/${homeState}/details`]:
                    data.moreContext.trim() !== '' ? data.moreContext : null,
                //#endregion

                ...empSettingUpdates,

                // Remove incomplete accreditation docs
                ...incompleteAccreditationDocs.reduce(
                    (a, c) => ({ ...a, [`accreditation/${c}`]: null }),
                    {}
                ),

                //#region Account State
                ['accountState/lastUpdated']: current.format(
                    'DD-MM-YYYY HH:mm:ss'
                ),
                [`accountState/submittedDate`]: current.format('DD-MM-YYYY'),
                [`accountState/submittedPlatform`]: 'web',
                [`accountState/value`]: 'submitted',
                //#endregion
            }

            return update(ref(db, `teachers/${userId}`), dbUpdates)
        } catch (e) {
            dispatch(setErrorMsg(getError(e)))
        }
    }

    return (
        <Stack className={styles.declaration} mt={5}>
            <Stack className={styles.title}>
                <h6>Pre-Employment Declaration</h6>
                <p>
                    All applicants must declare if they have received a formal
                    employment limitation notice from any school, childcare or
                    other education institution that restricts their ability to
                    work.
                </p>
            </Stack>
            <Controller
                control={control}
                name="hasLimitations"
                render={(props) => (
                    <>
                        <Stack
                            className={`${styles.noLimitation} ${
                                hasLimitation !== undefined && !hasLimitation
                                    ? styles['noLimitation--checked']
                                    : ''
                            }`}
                            onClick={() => props.field.onChange(false)}
                        >
                            <Checkbox
                                checked={
                                    props.field.value !== undefined
                                        ? !props.field.value
                                        : false
                                }
                                icon={
                                    // prettier-ignore
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_660_5685)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_660_5685"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                }
                                checkedIcon={
                                    // prettier-ignore
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_660_7000)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#60C1A3" />{' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#60C1A3" />{' '} <path d="M18.6663 7L9.49967 16.1667L5.33301 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_660_7000"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                }
                            />
                            <small>
                                I have not received any employment limitation
                                notices that restrict my working rights in
                                education institutions.
                            </small>
                        </Stack>
                        <Stack
                            className={`${styles.hasLimitation} ${
                                props.field.value !== undefined &&
                                props.field.value
                                    ? styles['hasLimitation--checked']
                                    : ''
                            }`}
                            onClick={() => props.field.onChange(true)}
                        >
                            <Checkbox
                                checked={
                                    props.field.value !== undefined
                                        ? props.field.value
                                        : false
                                }
                                icon={
                                    // prettier-ignore
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_660_5685)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_660_5685"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                }
                                checkedIcon={
                                    // prettier-ignore
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_10_402)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#FEC565" />{' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#FEC565" />{' '} <path d="M18.6663 7L9.49967 16.1667L5.33301 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_10_402"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                }
                            />
                            <small>
                                I have received notice of one or more employment
                                limitations that restrict me from working in
                                specific education institutions.
                            </small>
                        </Stack>
                    </>
                )}
            />

            <Stack gap={pxToRem(12)}>
                <Typography>
                    Additional Context{!!hasLimitation ? '' : ' (Optional)'}
                </Typography>
                <FormInputText
                    control={control}
                    name="moreContext"
                    multiline
                    rows={5}
                    placeholder="Enter text here..."
                    label=""
                    sx={SchoolRegSx}
                />
                <small>
                    Characters: Min 50, Max 400, Entered {moreContext.length}
                </small>
            </Stack>
            <Stack
                direction={'row'}
                alignItems={'center'}
                gap={3}
                sx={{ marginTop: 2.5 }}
            >
                <Controller
                    control={control}
                    name="consent"
                    render={(props) => (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value={props.field.value}
                                    onChange={() =>
                                        props.field.onChange(!props.field.value)
                                    }
                                    icon={
                                        // prettier-ignore
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_660_5685)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#A3A3A3" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_660_5685"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                    }
                                    checkedIcon={
                                        // prettier-ignore
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" > {' '} <g clipPath="url(#clip0_660_7602)"> {' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" fill="#2DC5F4" />{' '} <rect x="0.5" y="0.5" width="23" height="23" rx="3.5" stroke="#2DC5F4" />{' '} <path d="M18.6663 7L9.49967 16.1667L5.33301 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />{' '} </g>{' '} <defs> {' '} <clipPath id="clip0_660_7602"> {' '} <rect width="24" height="24" fill="white" />{' '} </clipPath>{' '} </defs>{' '} </svg>
                                    }
                                />
                            }
                            sx={{
                                '& .MuiTypography-root': {
                                    color: styles.shades100,
                                    fontSize: pxToRem(14),
                                    fontWeight: 400,
                                    lineHeight: pxToRem(20),
                                    letterSpacing: pxToRem(-0.28),
                                },
                            }}
                            label={`I consent for my basic information to be shared
                                    with education institutions for the purpose of
                                    verifying my right to work as a casual employee
                                    of Spark Relief Teachers in their school or
                                    education institution.`}
                        />
                    )}
                />
            </Stack>
            <Divider flexItem />
            <Stack className={styles.footer}>
                <PrimaryOutlinedButton
                    sx={{
                        minWidth: isMobile ? '0 !important' : undefined,
                        flex: isMobile ? 1 : undefined,
                    }}
                    onClick={onBack}
                    disabled={isSubmitting}
                >
                    BACK
                </PrimaryOutlinedButton>
                <PrimaryButton
                    sx={{
                        minWidth: isMobile ? '0 !important' : undefined,
                        flex: isMobile ? 1 : undefined,
                    }}
                    onClick={handleSubmit(onSubmit)}
                    loading={isSubmitting}
                    disabled={!isValid}
                >
                    SUBMIT APPLICATION
                </PrimaryButton>
            </Stack>
        </Stack>
    )
}

export const vicDeclarationFormRoles = z
    .enum(
        [
            'earlyLearningCentres', // ecc
            'victorianGovernmentSchools', // crt OR es
            'both', // crt AND ecc
        ] as const,
        'Applicant employment type not specified'
    )
    .describe('Employment type the applicant will be screened for')
export type VicDeclarationFormRole = z.infer<typeof vicDeclarationFormRoles>

export const vicDeclarationFormSchema = z.object({
    role: vicDeclarationFormRoles,
    firstName: z.string().trim(),
    lastName: z.string(),
    previousNames: z.string().optional(),
    dob: DayjsSchema,
    registration: z.discriminatedUnion('type', [
        z.object({
            type: z.literal('vit'),
            number: z
                .string('Incomplete*')
                .min(1, 'Incomplete*')
                // .regex(/^\d{6,7}$/, 'VIT numbers should be 6 or 7 digits')
                .describe('VIT registration number'),
        }),
        z.object({
            type: z.literal('wwcc'),
            number: z
                .string('Incomplete*')
                .min(1, 'Incomplete*')
                // Rules taken from https://service.vic.gov.au/services/working-with-children-check-status-checker/transaction#cardDetails
                // .regex(
                //     /^\d{7}[\da-zA-Z]$/,
                //     'Please enter the first 8 characters of your Working with Children Check card or application number. You can only use letters or numbers, no punctuation.'
                // )
                .describe('WWCC registration'),
        }),
    ]),

    // Yes no questions,
    hasReportableConductIssue: z.boolean(),
    hasEmploymentLimitation: z.boolean(),
    hasValidWwcc: z.boolean(),
    hasVitRegistration: z.boolean(),
    hasNoWorkRestrictions: z.boolean(),
    misconductTermination: z.object({
        answer: z.boolean(),
        context: z.string().optional(),
    }),

    consentToShareEligibility: z.literal(true),
    consentToShareInfo: z.literal(true),

    declarationByApplicant: z.literal(true),
})
export type VicDeclarationForm = z.infer<typeof vicDeclarationFormSchema>

const FormStack = styled(Stack)(({ theme }) => ({
    gap: theme.spacing(4),
    '&>.MuiStack-root': {
        gap: theme.spacing(2),
        '>.MuiTypography-root:first-of-type': {
            Typography: theme.typography.pl,
        },
    },
}))

export const VICDeclarationForm = ({ onBack }: IDeclartionForm) => {
    const {
        reset,
        handleSubmit,
        control,
        formState: { isValid, isSubmitting, errors },
        watch,
    } = useForm({
        resolver: zodResolver(vicDeclarationFormSchema),
        mode: 'onChange',
    })
    const registration = watch('registration')

    const employmentSetting = useSelector(selectTeacherEmploymentSettings)
    const names = useSelector(selectTeacherNames)
    const dob = useSelector(selectTeacherDob)
    const vit = useSelector(
        selectTeacherAccreditationDocumentByTypes([
            'vitTicket',
            'earlyChildcareVit',
        ])
    )

    logger.debug(`dob`, dob)
    logger.debug(`vit`, vit)

    useEffect(() => logger.debug(errors), [errors])

    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [loading, setLoading] = useState(true)
    const hasAppliedDefault = useRef(false)
    useEffect(() => {
        if (hasAppliedDefault.current) return
        if (
            employmentSetting.status !== 'success' ||
            dob === undefined ||
            names.status !== 'success' ||
            vit.loading
        )
            return

        if (dob === null) {
            dispatch(
                setErrorMsg(
                    'Date of birth provided in identification documents are invalid'
                )
            )
            navigate('my-account', { replace: true })
            return
        }

        const screeningRole = getScreeningRole(employmentSetting.data)
        if (screeningRole === null) {
            dispatch(setErrorMsg('Invalid employment setting!'))
            navigate('my-account', { replace: true })
            return
        }
        const { role, registration } = screeningRole
        reset({
            role,
            firstName:
                names.data.firstNameActual.value || names.data.firstName.value,
            lastName: names.data.lastName.value,
            dob: dayjs(dob, ['DD-MM-YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD']),
            registration: {
                type: registration,
                number: vit.data?.reference || '',
            },
        })
        hasAppliedDefault.current = true
        setLoading(false)
    }, [reset, dob, vit, employmentSetting, names, dispatch, navigate])

    const theme = useTheme()

    const userId = useSelector(selectUserUid)
    const incompleteAccreditationDocs = useSelector(
        selectIncompleteAccreditationDocIds
    )

    const onSubmit = async (data: VicDeclarationForm) => {
        try {
            // Add delay
            await new Promise((res) => setTimeout(res, 3000))
            // Submit declaration
            if (!userId) {
                throw new Error('User not authenticated')
            }

            // Submit dec form
            await TEACHER_API.onboarding.submitVicDecForm(data)

            const empSettingUpdates = Object.keys(
                employmentSetting.data || {}
            ).reduce(
                (a, c) => ({
                    ...a,
                    [`employmentSetting/${c}/state`]: 'submitted',
                }),
                {}
            )
            const current = dayjs().tz()

            const dbUpdates: Record<string, any> = {
                ...empSettingUpdates,
                // Remove incomplete accreditation docs
                ...incompleteAccreditationDocs.reduce(
                    (a, c) => ({ ...a, [`accreditation/${c}`]: null }),
                    {}
                ),

                //#region Account State
                ['accountState/lastUpdated']: current.format(
                    'DD-MM-YYYY HH:mm:ss'
                ),
                [`accountState/submittedDate`]: current.format('DD-MM-YYYY'),
                [`accountState/submittedPlatform`]: 'web',
                [`accountState/value`]: 'submitted',
                //#endregion
            }

            return update(ref(db, `teachers/${userId}`), dbUpdates)
        } catch (e) {
            captureException(e, {
                tags: {
                    feature: 'vicDeclarationForm',
                },
                level: 'error',
            })
            dispatch(setErrorMsg(getError(e)))
        }
    }

    if (loading)
        return (
            <LoadingSpinner
                fillViewPortHeight
                fillViewPortWidth
                id="vic-dec-form"
            />
        )

    return (
        <Box sx={{ p: 5 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <FormStack>
                    <Stack gap={0.5}>
                        <Typography variant="h6">
                            Employment Screening Consent Form
                        </Typography>
                        <Typography color={theme.palette.neutrals500.main}>
                            All applicants must declare if they have received a
                            formal employment limitation notice from any school,
                            childcare or other education institution that
                            restricts their ability to work.
                        </Typography>
                    </Stack>

                    <Stack>
                        <Typography>
                            Employment type applicant will be screened for
                        </Typography>
                        <Controller
                            control={control}
                            name="role"
                            render={(props) => (
                                <Stack
                                    gap={1}
                                    sx={{
                                        svg: { overflow: 'visible' },
                                        '& .MuiFormControlLabel-label': {
                                            typography: theme.typography.ps,
                                        },
                                    }}
                                >
                                    <FormControlLabel
                                        label="Early Learning Centres"
                                        checked={
                                            props.field.value ===
                                            'earlyLearningCentres'
                                        }
                                        disabled={
                                            props.field.value !==
                                            'earlyLearningCentres'
                                        }
                                        control={<CheckComponent readOnly />}
                                    />
                                    <FormControlLabel
                                        label="Victorian Schools"
                                        checked={
                                            props.field.value ===
                                            'victorianGovernmentSchools'
                                        }
                                        disabled={
                                            props.field.value !==
                                            'victorianGovernmentSchools'
                                        }
                                        control={<CheckComponent readOnly />}
                                    />
                                    <FormControlLabel
                                        label="Both"
                                        checked={props.field.value === 'both'}
                                        disabled={props.field.value !== 'both'}
                                        control={<CheckComponent readOnly />}
                                    />
                                </Stack>
                            )}
                        />
                    </Stack>

                    <Divider flexItem />

                    <Stack>
                        <Typography>Your Details</Typography>
                        <Stack gap={1.5}>
                            <FormInputText
                                control={control}
                                name="firstName"
                                label={null}
                                readonly
                                sx={UserRegSx}
                            />
                            <FormInputText
                                control={control}
                                name="lastName"
                                label={null}
                                readonly
                                sx={UserRegSx}
                            />
                            <FormInputText
                                control={control}
                                name="previousNames"
                                label={null}
                                placeholder="Previously used names, if applicable"
                                sx={UserRegSx}
                            />
                        </Stack>
                    </Stack>
                    <Stack>
                        <Typography>Date of birth</Typography>
                        <FormDatePicker_v2
                            control={control}
                            name="dob"
                            datePickerProps={{
                                // label: 'Date of birth',
                                format: 'DD/MM/YYYY',
                                sx: UserRegSx,
                            }}
                        />
                    </Stack>
                    <Stack>
                        {registration === undefined ? (
                            <Skeleton />
                        ) : registration.type === 'vit' ? (
                            <Typography>VIT registration number:</Typography>
                        ) : (
                            <Typography>WWCC registration</Typography>
                        )}
                        <FormInputText
                            control={control}
                            name="registration.number"
                            label={null}
                            placeholder={
                                registration.type === 'vit' ? 'VIT' : 'WWCC'
                            }
                            sx={UserRegSx}
                        />
                    </Stack>

                    <Divider flexItem />

                    {/* <Stack>
                        <Typography>
                            I consent to the Department of Education providing
                            the agency I am registered with information
                            regarding my eligibility for employment in Victorian
                            government schools and/or Victorian government early
                            learning centres.
                        </Typography>
                        <YesNo
                            control={control}
                            name="consentToShareEligibility"
                            noYellow
                        />
                    </Stack>
                    <Divider flexItem /> */}

                    <Stack>
                        <Typography>
                            I have been informed that I have been found to have
                            engaged or the subject of a current investigation
                            into the allegation of harm under the Reportable
                            Conduct Scheme
                        </Typography>
                        <YesNo
                            control={control}
                            name="hasReportableConductIssue"
                            yesYellow
                        />
                    </Stack>
                    <Divider flexItem />

                    <Stack>
                        <Typography>
                            I have been informed that I am subject to an
                            Employment Limitation with the Department of
                            Education
                        </Typography>
                        <YesNo
                            control={control}
                            name="hasEmploymentLimitation"
                            yesYellow
                        />
                    </Stack>
                    <Divider flexItem />

                    <Stack>
                        <Typography>
                            I confirm that I am eligible to work with children
                            in Victoria as I hold a valid Victorian Working with
                            Children's Check (WWCC) or VIT
                        </Typography>
                        <YesNo control={control} name="hasValidWwcc" noYellow />
                    </Stack>
                    <Divider flexItem />

                    <Stack>
                        <Typography>
                            I confirm that I am eligible to work in Victorian
                            schools and hold a current Victorian Institute of
                            Teaching (VIT) registration
                        </Typography>
                        <YesNo
                            control={control}
                            name="hasVitRegistration"
                            noYellow
                        />
                    </Stack>
                    <Divider flexItem />

                    <Stack>
                        <Typography>
                            I confirm that I have no legal or employment
                            restrictions in place preventing me to work with a
                            specific school or early learning centre
                        </Typography>
                        <YesNo
                            control={control}
                            name="hasNoWorkRestrictions"
                            noYellow
                        />
                    </Stack>
                    <Divider flexItem />

                    <Stack>
                        <Typography>
                            Has your employment with any previous employer been
                            terminated because of misconduct in your employment
                            or profession? If yes, please provide details of
                            where and why?
                        </Typography>
                        <YesNo
                            control={control}
                            name="misconductTermination.answer"
                            yesYellow
                        />
                    </Stack>

                    <Stack>
                        <Typography>Additional Context (Optional)</Typography>
                        <FormInputText
                            control={control}
                            name="misconductTermination.context"
                            label={null}
                            placeholder="Enter text here..."
                            multiline
                            rows={5}
                            sx={UserRegSx}
                        />
                    </Stack>
                    <Divider flexItem />

                    <Typography sx={{ fontWeight: 600 }}>
                        Consent to share information
                    </Typography>

                    <Controller
                        control={control}
                        name="consentToShareEligibility"
                        render={(props) => (
                            <FormControlLabel
                                checked={props.field.value || false}
                                onChange={props.field.onChange}
                                control={<CheckComponent />}
                                slotProps={{
                                    typography: {
                                        variant: 'ps',
                                        color: theme.palette.neutrals[500],
                                    },
                                }}
                                label={
                                    <>
                                        I consent to the Victorian Department of
                                        Education providing Spark Relief
                                        Teachers with information regarding my
                                        eligibility for employment in Victorian
                                        government schools and/or Victorian
                                        government early learning centres.
                                    </>
                                }
                            />
                        )}
                    />
                    <Divider flexItem />

                    <Controller
                        control={control}
                        name="consentToShareInfo"
                        render={(props) => (
                            <FormControlLabel
                                checked={props.field.value || false}
                                onChange={props.field.onChange}
                                control={<CheckComponent />}
                                slotProps={{
                                    typography: {
                                        variant: 'ps',
                                        color: theme.palette.neutrals[500],
                                    },
                                }}
                                label={
                                    <>
                                        I consent for my basic information to be
                                        shared with education institutions for
                                        the purpose of verifying my right to
                                        work as a casual employee of Spark
                                        Relief Teachers in their school or
                                        education institution.
                                    </>
                                }
                            />
                        )}
                    />
                    <Divider flexItem />

                    <Stack>
                        <Typography sx={{ fontWeight: 600 }}>
                            Declaration by applicant
                        </Typography>
                        <Controller
                            control={control}
                            name="declarationByApplicant"
                            render={(props) => (
                                <FormControlLabel
                                    checked={props.field.value || false}
                                    onChange={props.field.onChange}
                                    control={<CheckComponent />}
                                    slotProps={{
                                        typography: {
                                            variant: 'ps',
                                            color: theme.palette.neutrals[500],
                                        },
                                    }}
                                    label={
                                        <>
                                            I declare that the information
                                            provided in this form is true and
                                            accurate.
                                        </>
                                    }
                                />
                            )}
                        />
                    </Stack>

                    <Stack className={styles.footer}>
                        <Button
                            onClick={onBack}
                            variant="text"
                            color="primaryBlue500"
                        >
                            Back
                        </Button>
                        <PrimaryButton
                            type="submit"
                            loading={isSubmitting}
                            disabled={!isValid}
                            sx={{ alignSelf: 'center', width: 400 }}
                        >
                            Submit Form
                        </PrimaryButton>
                    </Stack>
                </FormStack>
            </form>
        </Box>
    )
}

const YesNo = <T extends FieldValues>({
    control,
    name,
    yesYellow,
    noYellow,
}: {
    control: Control<T>
    name: Path<T>
    yesYellow?: boolean
    noYellow?: boolean
}) => (
    <Controller
        control={control}
        name={name}
        render={(props) => (
            <Stack direction={'row'} alignItems={'center'} gap={5}>
                <FormControlLabel
                    checked={
                        props.field.value !== undefined
                            ? Boolean(props.field.value)
                            : false
                    }
                    onClick={() => props.field.onChange(true)}
                    control={
                        <CheckComponent
                            checkedIcon={
                                yesYellow ? YellowCheckedIcon : GreenCheckedIcon
                            }
                        />
                    }
                    label={'Yes'}
                />
                <FormControlLabel
                    checked={
                        props.field.value !== undefined
                            ? !Boolean(props.field.value)
                            : false
                    }
                    onClick={() => props.field.onChange(false)}
                    control={
                        <CheckComponent
                            checkedIcon={
                                noYellow ? YellowCheckedIcon : GreenCheckedIcon
                            }
                        />
                    }
                    label={'No'}
                />
            </Stack>
        )}
    />
)

const getScreeningRole = (
    settings: EmploymentSettings
): {
    role: VicDeclarationForm['role']
    registration: 'vit' | 'wwcc'
} | null => {
    let isCrt: boolean = false
    let isECT: boolean = false
    let isNonECT: boolean = false
    let isEs: boolean = false

    Object.values(settings).forEach((el) => {
        switch (el.type) {
            case 'school': {
                isCrt = true
                break
            }
            case 'supportStaff': {
                isEs = true
                break
            }
            case 'earlyChildcare': {
                if (el.level === 'earlyChildhoodTrained') {
                    isECT = true
                } else {
                    isNonECT = true
                }
                break
            }
        }
    })

    if (isCrt && (isECT || isNonECT))
        return { role: 'both', registration: 'vit' }
    if (isEs || isCrt)
        return { role: 'victorianGovernmentSchools', registration: 'vit' }
    if (isECT) {
        return { role: 'earlyLearningCentres', registration: 'vit' }
    } else if (isNonECT) {
        return { role: 'earlyLearningCentres', registration: 'wwcc' }
    }
    return null
}
